import { useContext, useEffect, useState } from 'react';
import { KeyboardContext, KeyboardTrigger, LayoutContent } from './Layout';
import { PageProps, lBlueFilter } from './operation/OperationPage';
import Button from './PendantButton';
import { ApplicationContext, ConfigurationsContext } from '../App';
import {
	BoxPosition_t,
	Box_t,
	Layer_t,
	Layout_t,
	MachineConfig,
	PalletConfigObj,
	PalletConfiguration,
	Pallet_t,
	SlaveSystemDescription,
	SlipSheet_t,
	Stack,
	newPalletConfiguration,
} from 'blackbox';
import { Application } from 'libapi';
import { Scroll, useMachineConfig } from './General';
import PlusIcon from './pallet_config/images/plus.svg';
import { COLOR, getChangeWrapperString } from './shared/shared';
import close from '../images/close.png';
import generic_robot from '../images/genericrobot.png';
import LayerImage from './pallet_config/images/pallet-layers.png';
import CheckBox, { lWhiteStyle } from './common/Checkbox';
import checkmark from '../images/checkmark.png';
import { ItemFilter } from './pallet_config/Shared';

function generateSubConfig(
	config: PalletConfiguration,
	selectedStacks: { [key: string]: string[] }
): PalletConfiguration {
	const subConfig = newPalletConfiguration('');
	subConfig.pallets = [];

	const uniqueBoxIds = new Set();
	const uniqueSSIds = new Set();

	Object.keys(selectedStacks).forEach(palletId => {
		// For each pallet
		// go into the config...go to the pallet with the same id
		const palletIdx = config.pallets.findIndex(p => p.id === palletId);
		const lNewPallet: Pallet_t = JSON.parse(
			JSON.stringify(config.pallets[palletIdx])
		);

		// Remove any stacks that aren't in stackIds
		const stackIds = selectedStacks[palletId];

		lNewPallet.Stack = lNewPallet.Stack.filter(stack =>
			stackIds.includes(stack.id)
		);

		// Grab the layouts with the ids on each stack.
		// We assume all layers have the same layout in the stack. So just take the first one.
		const layoutIds = lNewPallet.Stack.map(stack => stack.layers[0].layout_id);
		lNewPallet.Layouts = lNewPallet.Layouts.filter(layout =>
			layoutIds.includes(layout.id)
		);

		// Grab the box ids
		for (let l = 0; l < lNewPallet.Layouts.length; l++) {
			uniqueBoxIds.add(lNewPallet.Layouts[l].box_id);
		}

		// // Grab the ss ids
		// for (let s = 0; s < lNewPallet.Stack.length; s++) {
		// 	const ids = lNewPallet.Stack[s].slipSheet?.ids;

		// 	if (ids) {
		// 		const keys = Object.keys(ids);
		// 		keys.forEach(key => {
		// 			let k = Number(key);
		// 			uniqueSSIds.add(ids[k]);
		// 		});
		// 	}
		// }

		subConfig.pallets.push(lNewPallet);
	});

	// Snag the boxes and slipsheets onto the new config
	const lNewBoxes: Box_t[] = [];
	config.boxes.forEach((box: Box_t) => {
		if (uniqueBoxIds.has(box.id)) {
			lNewBoxes.push(box);
		}
	});

	// const lNewSS: SlipSheet_t[] = [];
	// config.slipsheets.forEach((ss: SlipSheet_t) => {
	// 	if (uniqueSSIds.has(ss.id)) {
	// 		lNewSS.push(ss);
	// 	}
	// });

	subConfig.boxes = lNewBoxes;

	// Just copy all the slip sheets...idc. Avoid issues
	subConfig.slipsheets = JSON.parse(JSON.stringify(config.slipsheets));

	// Any indexes that refrence things need to be updated with their new index now...
	subConfig.pallets.forEach(pallet => {
		pallet.Layouts.forEach(layout => {
			const box_idx = subConfig.boxes.findIndex(b => b.id === layout.box_id);
			layout.boxPositions.forEach(bp => {
				bp.index = box_idx;
			});
		});
		pallet.Stack.forEach(stack => {
			stack.layers.forEach(layer => {
				const layout_idx = pallet.Layouts.findIndex(
					layout => layout.id === layer.layout_id
				);
				layer.index = layout_idx;
			});
		});
	});

	return subConfig;
}

interface CreateEditCollectionDialogProps {
	pExit: (pCreated: boolean) => void;
	pEditingCollection?: PalletConfigObj | null;
	pEditingCollectionId?: string | null;
}

interface CollectionMetaData_t {
	name: string | undefined;
	numSkus: number;
	numStacks: number;
}

// TODO Add pagination of render if needed...
function CreateEditCollectionDialog({
	pExit,
	pEditingCollection,
	pEditingCollectionId,
}: CreateEditCollectionDialogProps) {
	const lApp = useContext(ApplicationContext) as Application;
	const { all_configs: lConfigs } = useContext(ConfigurationsContext) as any;
	const lPalletConfig = lConfigs as PalletConfigObj;
	useEffect(() => {
		if (!lPalletConfig.config) return;
	}, [lPalletConfig]);

	// { palletid: [stackids]}
	const [lSelectedStacks, lSetSelectedStacks] = useState<{
		[key: string]: string[];
	}>({});

	const [lCollectionMetaData, lSetCollectionMetaData] =
		useState<CollectionMetaData_t>({
			name: undefined,
			numSkus: 0,
			numStacks: 0,
		});

	const [lFinishedLoad, lSetFinishLoad] = useState<boolean>(false);

	useEffect(() => {
		if (!pEditingCollection || !pEditingCollectionId || !lPalletConfig) return;
		if (lFinishedLoad) return;
		try {
			const meta: CollectionMetaData_t = JSON.parse(
				pEditingCollection.config.name
			);
			const lTempMeta = { ...lCollectionMetaData };
			lTempMeta.name = meta.name;
			lSetCollectionMetaData(lTempMeta);

			const lNewSelected: { [key: string]: string[] } = {};
			// Attempt to find matching stacks in pallet config...The pallet and stack indexes from the collection won't match.

			const lAllStackIdsInMasterConfig: string[] = [];
			lPalletConfig.config.pallets.forEach(p => {
				p.Stack.forEach(s => {
					lAllStackIdsInMasterConfig.push(s.id);
				});
			});
			const pallets = pEditingCollection.config.pallets;
			for (const pallet of pallets) {
				const stacks = pallet.Stack;
				for (const stack of stacks) {
					// If stack exists in master config...then select it.
					if (lAllStackIdsInMasterConfig.includes(stack.id)) {
						(lNewSelected[pallet.id] = lNewSelected[pallet.id] || []).push(stack.id);
					}
				}
			}

			lSetFinishLoad(true);
			lSetSelectedStacks(lNewSelected);
		} catch (e) {
			lSetFinishLoad(true);
			// pExit(false);
			console.error('Error parsing fetched collection during edit.', e);
		}
	}, [pEditingCollectionId, lPalletConfig]);

	function handleStackClick(
		pPalletID: string,
		pStackID: string,
		pSelected: boolean
	) {
		const lCp = { ...lSelectedStacks };

		if (pSelected) {
			const idx = lCp[pPalletID].findIndex(sid => sid === pStackID);
			if (idx !== -1) {
				lCp[pPalletID].splice(idx, 1);
			}
		} else {
			if (!Object.keys(lCp).includes(pPalletID)) {
				lCp[pPalletID] = [];
			}

			if (!lCp[pPalletID].includes(pStackID)) {
				lCp[pPalletID].push(pStackID);
			}
		}

		lSetSelectedStacks(lCp);
	}

	function save_collection() {
		const lEditing = !!pEditingCollectionId;

		// Create a new PalletConfiguration
		const lNewConfig = generateSubConfig(lPalletConfig.config, lSelectedStacks);

		// get stack and sku count
		const lMeta = { ...lCollectionMetaData };
		lMeta.numSkus = lNewConfig.boxes.length;
		for (const pallet of lNewConfig.pallets) {
			lMeta.numStacks += pallet.Stack.length;
		}

		// Using name as a general meta data property
		lNewConfig.name = JSON.stringify(lMeta);

		if (lEditing) {
			console.log('ID!!!', pEditingCollectionId);
			lApp.api.Collections.edit(lNewConfig, pEditingCollectionId)
				.then(() => {
					pExit(true);
				})
				.catch(e => {
					alert('Error editing collection.' + e);
				});
		} else {
			lApp.api.Collections.post(lNewConfig)
				.then(() => {
					pExit(true);
				})
				.catch(e => {
					alert('Error creating collection.' + e);
				});
		}
	}

	const stackSelected = (pPalletId: string, pStackId: string) => {
		return !!lSelectedStacks[pPalletId]?.includes(pStackId);
	};

	const lKeyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);
	const lNameHandlers = getChangeWrapperString(function (pNewName: string) {
		const lCp = { ...lCollectionMetaData };
		lCp.name = pNewName;
		lSetCollectionMetaData(lCp);
	});
	const lTriggerKeyboardName = function () {
		if (lKeyboardTrigger) {
			lKeyboardTrigger(
				'Enter a name for your Collection:',
				lCollectionMetaData.name || '',
				lNameHandlers.Direct
			);
		}
	};

	const [lSearchFilter, lSetSearchFilter] = useState<string>('');
	const lUpdateSearchFilter = function (pNewFilter: string) {
		lSetSearchFilter(pNewFilter);
	};

	const enable_save =
		Object.values(lSelectedStacks).some(
			list => Array.isArray(list) && list.length > 0
		) &&
		lCollectionMetaData?.name &&
		lCollectionMetaData.name.length > 0;

	return (
		<LayoutContent pTab={{ Name: '', IconClass: '' }} dialog={true}>
			<div className="full-size flex-col align-center flex-between">
				<header className="height-7 background-silver full-width rounded-top padding-bottom flex-row flex-between align-center">
					<div className="full-height flex-row align-center double-padding-top">
						<h1 className="font-5 bold-font color-blue double-padding-left">
							{`${
								!!pEditingCollection?.config
									? 'Edit Collection'
									: 'Create a New Collection'
							}`}
						</h1>
					</div>
					<Button
						pAction={() => {
							pExit(false);
						}}
						pNormalColorOverride={COLOR.TRANSPARENT}
						className="double-margin-top height-60 double-margin-right"
					>
						<img
							className="full-height auto-width"
							style={{ filter: lBlueFilter }}
							src={close}
							alt="close"
						/>
					</Button>
				</header>
				<section className="height-7 background-silver full-width rounded-top padding-bottom flex-row flex-between align-center">
					<div className="full-height flex-row align-center double-padding-top">
						<span className="font-5 color-blue double-padding-left">
							Collection Name:
						</span>
						<input
							className="half-padding margin-left"
							type="text"
							value={lCollectionMetaData.name || ''}
							onChange={lNameHandlers.Event}
							onClick={lTriggerKeyboardName}
						/>
					</div>
					<div className="full-height flex-row align-center double-padding-top double-margin-right">
						<ItemFilter
							pSearchFilter={lSearchFilter}
							pSetSearchFilter={lUpdateSearchFilter}
							triggerKeyboard={lKeyboardTrigger}
						/>
						{lSearchFilter.length > 0 && (
							<Button pAction={() => lSetSearchFilter('')} className="margin-left">
								<span>Clear</span>
							</Button>
						)}
					</div>
				</section>
				<main className="height-93 full-width flex-col flex-between align-center double-padding-top">
					<Scroll className="full-width flex-col align-center">
						{!!lPalletConfig.config &&
							(function () {
								const lRegExp = new RegExp(lSearchFilter.trim().toLowerCase());
								return lPalletConfig.config.pallets.map((pPallet, pPalletIdx) => {
									const lStacks = pPallet.Stack || [];
									const lSearchedStacks = lStacks.filter(function (stack: Stack) {
										const lFirstLayoutIndex = stack.layers[0].index;
										const lFirstLayout = pPallet.Layouts[lFirstLayoutIndex];
										const lFirstBoxIndex = lFirstLayout.boxPositions[0].index;
										const lFirstBox = lPalletConfig.config.boxes[lFirstBoxIndex];
										return (
											lRegExp.test(stack.name.toLowerCase()) ||
											lRegExp.test(pPallet.name.toLowerCase()) ||
											lRegExp.test(lFirstLayout.name.toLowerCase()) ||
											lRegExp.test(lFirstBox.name.toLowerCase())
										);
									});

									return lSearchedStacks.map((pStack, idx) => {
										const selected = stackSelected(pPallet.id, pStack.id);

										const lBox =
											lPalletConfig.config.boxes[
												pPallet.Layouts[pStack.layers[0].index].boxPositions[0].index
											];

										return (
											<div
												key={idx}
												className={
													'flex-row align-center flex-between background-desert-storm dark-border width-98 rounded margin-bottom vh-8' +
													(selected ? ' shadow' : '')
												}
												onClick={() => {
													handleStackClick(pPallet.id, pStack.id, selected);
												}}
											>
												<div className="full-height flex-row align-center">
													<img
														className="full-height auto-width height-80 double-margin-left rounded"
														src={LayerImage}
														alt="generic robot"
													/>
													<div className="padding"></div>
													<span className="double-margin-left double-padding-top double-padding-bottom font-5">
														{'Stack: ' + pStack.name}
													</span>
													<span className="double-margin-left font-5">{`Box: ${lBox.name}`}</span>
													<span className="double-margin-left font-5">{pPallet.name}</span>
												</div>

												<div
													className={`fully-round ${
														selected
															? 'background-light-blue '
															: 'background-silver inner-border'
													} square center vw-2 double-margin-right`}
												>
													{selected && (
														<img
															className="auto-height width-100"
															style={{ filter: lWhiteStyle }}
															src={checkmark}
															alt="checkmark"
														/>
													)}
												</div>
											</div>
										);
									});
								});
							})()}
					</Scroll>
					<div className="full-width flex-row flex-between double-margin-bottom">
						<Button pAction={() => pExit(false)} pClass="double-margin-left width-10">
							<span>Cancel</span>
						</Button>
						<Button
							pAction={() => {
								save_collection();
							}}
							pClass="double-margin-right width-10"
							pDisabled={!enable_save}
							pClickColorOverride={COLOR.GREEN}
							pNormalColorOverride={enable_save ? COLOR.LIGHT_BLUE : COLOR.DARK_GREY}
						>
							<span>{'Save'}</span>
						</Button>
					</div>
				</main>
			</div>
		</LayoutContent>
	);
}

interface DeployProps {
	pDesc: CollectionDescription;
	pExit: () => void;
	pMachineConfig: MachineConfig;
}

function DeployCollectionDialog({ pDesc, pExit, pMachineConfig }: DeployProps) {
	const lApp = useContext(ApplicationContext) as Application;

	const lDeployingText = 'Deploying...';
	const lNotDeployText = 'Deploy';

	const [lSelectedMachines, lSetSelectedMachines] = useState<string[]>([]);
	const [lDeployButtonText, lSetDeployButtonText] =
		useState<string>(lNotDeployText);
	const [lDeployButtonColor, lSetDeployButtonColor] = useState<COLOR>(
		COLOR.LIGHT_BLUE
	);

	const [lDeployError, lSetDeployError] = useState<string | undefined>(void 0);

	function send_collection() {
		// if (!pDesc.id) return;
		lSetDeployButtonText(lDeployingText);
		lSetDeployButtonColor(COLOR.DARK_GREY);
		lApp.api.Collections.deploy(pDesc.id, lSelectedMachines)
			.then(() => {
				lSetDeployButtonColor(COLOR.LIGHT_BLUE);
				lSetDeployButtonText('Success!');
				setTimeout(() => {
					pExit();
				}, 1000);
			})
			.catch(e => {
				console.log('ERROR!!!', e.response.data);
				console.error(e, e.response.data);
				let eMsg = 'Something went wrong during deployment';
				if (e?.response?.data) {
					eMsg = e.response.data;
				}
				lSetDeployError(eMsg);
				setTimeout(() => {
					lSetDeployButtonText(lNotDeployText);
					lSetDeployButtonColor(COLOR.LIGHT_BLUE);
				}, 1000);
			});
	}

	function handleSystemSelect(ip: string) {
		const lCp = [...lSelectedMachines];
		const idx = lCp.findIndex(s => s === ip);
		if (idx !== -1) {
			lCp.splice(idx, 1);
		} else {
			lCp.push(ip);
		}
		lSetSelectedMachines(lCp);
	}

	const selected = (ip: string) => lSelectedMachines.includes(ip);
	const any_selected = lSelectedMachines.length > 0;
	return (
		<>
			<LayoutContent pTab={{ Name: '', IconClass: '' }} dialog={true}>
				<div className="full-size flex-col align-center flex-between">
					<header className="height-9 background-silver full-width rounded-top padding-bottom flex-row flex-between align-center">
						<div className="full-height flex-row align-center double-padding-top">
							<h1 className="font-6 bold-font color-blue double-padding-left">
								Deploy Collection: '{pDesc.metadata.name}'
							</h1>
						</div>
						{lDeployButtonText !== lDeployingText && (
							<Button
								pAction={pExit}
								pNormalColorOverride={COLOR.TRANSPARENT}
								className="double-margin-top height-60 double-margin-right"
								pDisabled={lDeployButtonText === lDeployingText}
							>
								<img
									className="full-height auto-width"
									style={{ filter: lBlueFilter }}
									src={close}
									alt="close"
								/>
							</Button>
						)}
					</header>
					<main className="height-91 full-width flex-col flex-between align-center double-padding-top">
						<Scroll className="full-width flex-col align-center">
							<div className="full-width width-97">
								{(pMachineConfig?.SlaveSystems || []).map(
									(pSys: SlaveSystemDescription, pIdx) => {
										const s = selected(pSys.IP);
										return (
											<div
												key={pIdx}
												onClick={() => {
													handleSystemSelect(pSys.IP);
												}}
												className={
													'flex-row align-center flex-between full-width background-desert-storm dark-border rounded margin-bottom vh-8 ' +
													(s ? ' shadow' : '')
												}
											>
												<div className="full-height flex-row align-center">
													<img
														className="full-height auto-width height-86 half-margin-left more-rounded"
														src={generic_robot}
														alt="generic robot"
													/>
													<div className="double-padding"></div>
													<span className="double-padding font-5">{pSys.Name}</span>
													<span className="double-padding font-5">{pSys.IP}</span>
												</div>

												<div
													className={`fully-round ${
														s ? 'background-light-blue ' : 'background-silver inner-border'
													} square center vw-2 double-margin-right`}
												>
													{s && (
														<img
															className="auto-height width-100"
															style={{ filter: lWhiteStyle }}
															src={checkmark}
															alt="checkmark"
														/>
													)}
												</div>
											</div>
										);
									}
								)}
							</div>
						</Scroll>

						<div className="flex-row full-width flex-between double-padding-bottom">
							<Button
								pAction={pExit}
								pClass="double-margin-left width-10"
								pDisabled={lDeployButtonText === lDeployingText}
							>
								<span>Cancel</span>
							</Button>
							<Button
								pAction={send_collection}
								pNormalColorOverride={
									any_selected ? lDeployButtonColor : COLOR.DARK_GREY
								}
								pClickColorOverride={
									any_selected ? lDeployButtonColor : COLOR.DARK_GREY
								}
								pClass="double-margin-right width-10"
								pDisabled={!any_selected && lDeployButtonText === lNotDeployText}
							>
								<span>{lDeployButtonText}</span>
							</Button>
						</div>
					</main>
				</div>
			</LayoutContent>
			{!!lDeployError && (
				<ConfirmPopup
					pExit={_pConfirm => {
						lSetDeployError(void 0);
						pExit();
					}}
					pMsg={lDeployError}
				/>
			)}
		</>
	);
}

interface ConfirmPopupProps {
	pExit: (pConfirm: boolean) => void;
	pMsg: string;
}

function ConfirmPopup({ pExit, pMsg }: ConfirmPopupProps) {
	const lButtonClass = 'width-20 double-margin-left double-margin-right';
	return (
		<LayoutContent pTab={{ Name: '', IconClass: '' }} popup={true}>
			<div className="full-size flex-col align-center flex-between double-padding">
				<div className="padding"></div>
				<span className="font-5 color-blue bold-font text-align-center double-padding">
					{pMsg}
				</span>
				<div className="flex-row full-width flex-between">
					<Button pAction={() => pExit(false)} className={lButtonClass}>
						<span className="double-padding">Cancel</span>
					</Button>
					<Button
						pAction={() => pExit(true)}
						pNormalColorOverride={COLOR.LIGHT_BLUE}
						className={lButtonClass}
					>
						<span className="double-padding">Confirm</span>
					</Button>
				</div>
			</div>
		</LayoutContent>
	);
}

interface CollectionDescription {
	id: string;
	metadata: CollectionMetaData_t;
}

enum CollectionPageMode {
	NORMAL,
	CREATE_EDIT,
	DEPLOY,
	CONFIRM_DELETE,
}
function CollectionsPageContent() {
	const lApp = useContext(ApplicationContext) as Application;
	const { lMachineConfig } = useMachineConfig(lApp);

	const [lPageMode, lSetPageMode] = useState<CollectionPageMode>(
		CollectionPageMode.NORMAL
	);
	const [lActiveDeployCollection, lSetActiveDeployCollection] =
		useState<CollectionDescription | null>(null);

	const [lActiveEditCollection, lSetActiveEditCollection] =
		useState<PalletConfigObj | null>(null);
	const [lActiveEditCollectionId, lSetActiveEditCollectionId] = useState<
		string | null
	>(null);

	const [lConfirmDeleteCol, lSetConfirmDeleteCol] =
		useState<CollectionDescription | null>(null);

	const [lCollections, lSetCollections] = useState<CollectionDescription[]>([]); // Collections. Probably their ids and names etc. not the entire file.

	const lKeyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);
	const [lSearchFilter, lSetSearchFilter] = useState<string>('');
	const lUpdateSearchFilter = function (pNewFilter: string) {
		lSetSearchFilter(pNewFilter);
	};

	function fetch_and_set_collections() {
		lApp.api.Collections.get().then(pCollectionData => {
			if (!!pCollectionData) {
				const lParsed = [];
				for (const col of pCollectionData) {
					try {
						const lData = {
							id: col.id,
							metadata: JSON.parse(col.name),
						};
						lParsed.push(lData);
					} catch {
						console.error('Error reading collection', col);
					}
				}

				lSetCollections(lParsed);
				lSetPageMode(CollectionPageMode.NORMAL);
			}
		});
	}
	useEffect(() => {
		fetch_and_set_collections();
	}, []);

	async function fetch_config_from_id(id: string) {
		const res = await lApp.api.Collections.getCollection(id);
		if (!!res) {
			return res;
		}
		return {};
	}

	async function delete_collection(id: string) {
		const res = await lApp.api.Collections.delete(id);
		return res === 'OK';
	}

	const download_collection = async (colData: CollectionDescription) => {
		const cfg = await fetch_config_from_id(colData.id);
		console.log('CFG:', cfg);
		var downloadLink = document.createElement('a');
		downloadLink.href = window.URL.createObjectURL(
			new Blob([JSON.stringify(cfg)], {
				type: 'application/json',
			})
		);
		downloadLink.download = `${colData.metadata.name}.json`;
		downloadLink.click();
	};

	const handle_delete_collection = async (id: string) => {
		const res = await delete_collection(id);
		console.log('DELTE RES:', res);
		let lTemp = [...lCollections];
		lTemp = lTemp.filter(c => c.id !== id);
		lSetCollections(lTemp);
	};

	return (
		<div className="full-width vh-91">
			<div className="full-size grid-row-10 align-center">
				<header className="flex-row flex-between align-center full-size">
					<span className="double-margin-left med-big-font font-weight-bold color-blue">
						Collections
					</span>
					<div className="flex-row align-center full-height">
						<ItemFilter
							pSearchFilter={lSearchFilter}
							pSetSearchFilter={lUpdateSearchFilter}
							triggerKeyboard={lKeyboardTrigger}
						/>
						{lSearchFilter.length > 0 && (
							<Button pAction={() => lSetSearchFilter('')} className="margin-left">
								<span>Clear</span>
							</Button>
						)}
					</div>

					{/* <input
						className={`half-padding width-30 half-margin-top rounded background-silver color-dark-grey`}
						type="text"
						defaultValue={'Search'}
						onChange={() => {}}
						onClick={() => {}}
					/> */}
					<div
						className="flex-center cursor-pointer double-margin-right"
						onClick={() => {
							lSetPageMode(CollectionPageMode.CREATE_EDIT);
						}}
					>
						<PlusIcon />

						<h3 className="padding-left large-font hover-underline">
							Add New Collection
						</h3>
					</div>
				</header>
				{/* <div className="padding"></div> */}
				<Scroll className="full-size">
					<div className="flex-col align-center">
						<div className="padding"></div>
						{lCollections &&
							(function () {
								const lRegExp = new RegExp(lSearchFilter.trim());
								const lSearchedCollections = lCollections.filter(pCol =>
									!!pCol?.metadata.name ? lRegExp.test(pCol.metadata.name) : false
								);
								return lSearchedCollections.map((pColData, pIdx) => {
									return (
										<div
											key={pIdx}
											className="flex-row align-center flex-between full-width vh-8 width-94 margin-bottom"
										>
											<div className="flex-row width-95 vh-8 background-silver rounded align-center flex-between">
												<div className="flex-row full-height align-center width-70">
													<span className="double-margin-left width-30 bold-font color-blue">
														{pColData?.metadata.name}
													</span>

													<span className="double-margin-left width-30">
														{`${pColData?.metadata.numStacks} Stack${
															pColData?.metadata.numStacks === 1 ? '' : 's'
														}`}
													</span>

													<span className="double-margin-left width-30">
														{`${pColData?.metadata.numSkus} Sku${
															pColData?.metadata.numSkus === 1 ? '' : 's'
														}`}
													</span>
												</div>

												<div className="flex-row full-height align-center double-margin-right">
													<Button
														pAction={async () => {
															const cfg = await fetch_config_from_id(pColData.id);
															lSetPageMode(CollectionPageMode.CREATE_EDIT);
															lSetActiveEditCollection(cfg);
															lSetActiveEditCollectionId(pColData.id);
														}}
														pClass="vw-10 double-margin-right"
													>
														<span className="">Edit</span>
													</Button>
													<Button
														pAction={() => {
															lSetPageMode(CollectionPageMode.DEPLOY);
															lSetActiveDeployCollection({ ...pColData });
														}}
														pClass="vw-10 double-margin-right"
													>
														<span className="">Deploy</span>
													</Button>
													<div className="padding"></div>
													<div
														className="cursor-pointer font-large flex-center color-dark-grey hover-bold hover-underline"
														onClick={() => download_collection(pColData)}
													>
														<span className="large-font bold-font icon-save"></span>
													</div>
													<div className="padding"></div>
												</div>
											</div>

											<div
												className="double-margin-left cursor-pointer font-large flex-center color-red hover-bold hover-underline"
												onClick={() => {
													lSetConfirmDeleteCol(pColData);
													lSetPageMode(CollectionPageMode.CONFIRM_DELETE);
												}}
											>
												<span className="large-font bold-font icon-delete"></span>
											</div>
										</div>
									);
								});
							})()}
						<div className="full-width vh-8"></div>
						<div className="full-width vh-8"></div>
						{/* <div className="full-width vh-8"></div> */}
					</div>
				</Scroll>
			</div>
			{lPageMode === CollectionPageMode.CREATE_EDIT && (
				<CreateEditCollectionDialog
					pExit={(pCreated: boolean) => {
						lSetActiveEditCollection(null);
						lSetActiveEditCollectionId(null);
						if (pCreated) fetch_and_set_collections();
						else {
							lSetPageMode(CollectionPageMode.NORMAL);
						}
					}}
					pEditingCollection={lActiveEditCollection}
					pEditingCollectionId={lActiveEditCollectionId}
				/>
			)}
			{lPageMode === CollectionPageMode.DEPLOY &&
				lActiveDeployCollection &&
				!!lMachineConfig && (
					<DeployCollectionDialog
						pDesc={lActiveDeployCollection}
						pExit={() => {
							lSetPageMode(CollectionPageMode.NORMAL);
						}}
						pMachineConfig={lMachineConfig}
					/>
				)}
			{lPageMode === CollectionPageMode.CONFIRM_DELETE && !!lConfirmDeleteCol && (
				<ConfirmPopup
					pExit={(pConfirm: boolean) => {
						lSetPageMode(CollectionPageMode.NORMAL);
						if (pConfirm) {
							handle_delete_collection(lConfirmDeleteCol.id);
						}
					}}
					pMsg={`Are you sure you want to delete '${
						lConfirmDeleteCol.metadata.name || 'untitled'
					}'?`}
				/>
			)}
		</div>
	);
}

export default function CollectionsPage({ pTab }: PageProps) {
	return (
		<LayoutContent pTab={pTab} hideHeader={true}>
			<CollectionsPageContent />
		</LayoutContent>
	);
}
